import React, { useEffect, useState } from "react";

type FlashTone = "success" | "error";
type InputMode = "document" | "text";

interface PromptOption {
  id: number;
  key: string;
  name: string;
  description: string;
  promptBody: string;
  outputFormat: "text" | "json";
  jsonSchemaText: string;
  model: string;
  active: boolean;
}

interface DocumentInfo {
  id: number;
  title: string;
  typeLabel: string;
  sourceLabel: string;
  receivedAtLabel: string;
  contentType: string;
  fileStateLabel: string;
  detailUrl: string;
  openFileUrl?: string | null;
}

interface RunInfo {
  id: number;
  provider: string;
  model: string;
  outputFormat: "text" | "json";
  status: "queued" | "running" | "succeeded" | "failed";
  promptId?: number | null;
  promptName?: string | null;
  inputType: InputMode;
  inputLabel: string;
  documentId?: number | null;
  documentTitle?: string | null;
  sourceTextPreview?: string | null;
  durationLabel: string;
  queuedAtLabel: string;
  startedAtLabel: string;
  finishedAtLabel: string;
  promptSnapshot: string;
  responseText?: string | null;
  responseJsonText?: string | null;
  errorDetailsText?: string | null;
  usageCostLabel: string;
  detailUrl: string;
  statusUrl: string;
}

interface EditorState {
  promptId?: number | null;
  key: string;
  name: string;
  description: string;
  promptBody: string;
  outputFormat: "text" | "json";
  jsonSchemaText: string;
  model: string;
  active: boolean;
}

interface DocumentAiWorkspaceProps {
  authenticityToken: string;
  createRunUrl: string;
  createPromptUrl: string;
  promptUrlTemplate: string;
  promptOptions: PromptOption[];
  initialEditor: EditorState;
  modelOptions: string[];
  selectedDocument?: DocumentInfo | null;
  recentRuns: RunInfo[];
}

function flashClasses(tone: FlashTone) {
  return tone === "success"
    ? "rounded-xl border border-emerald-200/80 bg-emerald-50/80 px-4 py-3 text-sm text-emerald-900 dark:border-emerald-900/60 dark:bg-emerald-950/30 dark:text-emerald-100"
    : "rounded-xl border border-rose-200/80 bg-rose-50/80 px-4 py-3 text-sm text-rose-900 dark:border-rose-900/60 dark:bg-rose-950/30 dark:text-rose-100";
}

function statusClasses(status: RunInfo["status"]) {
  switch (status) {
    case "succeeded":
      return "inline-flex rounded-full border border-emerald-200/80 bg-emerald-50 px-2 py-0.5 text-xs font-semibold text-emerald-700 dark:border-emerald-900/60 dark:bg-emerald-950/30 dark:text-emerald-300";
    case "failed":
      return "inline-flex rounded-full border border-rose-200/80 bg-rose-50 px-2 py-0.5 text-xs font-semibold text-rose-700 dark:border-rose-900/60 dark:bg-rose-950/30 dark:text-rose-300";
    case "running":
      return "inline-flex rounded-full border border-sky-200/80 bg-sky-50 px-2 py-0.5 text-xs font-semibold text-sky-700 dark:border-sky-900/60 dark:bg-sky-950/30 dark:text-sky-300";
    default:
      return "inline-flex rounded-full border border-amber-200/80 bg-amber-50 px-2 py-0.5 text-xs font-semibold text-amber-700 dark:border-amber-900/60 dark:bg-amber-950/30 dark:text-amber-300";
  }
}

function sortPrompts(prompts: PromptOption[]) {
  return [...prompts].sort((left, right) => left.name.localeCompare(right.name) || left.id - right.id);
}

function normalizeErrors(payload: unknown): string {
  if (payload && typeof payload === "object" && Array.isArray((payload as { errors?: string[] }).errors)) {
    return (payload as { errors: string[] }).errors.join(", ");
  }

  return "Request failed.";
}

export default function DocumentAiWorkspace({
  authenticityToken,
  createRunUrl,
  createPromptUrl,
  promptUrlTemplate,
  promptOptions: initialPromptOptions,
  initialEditor,
  modelOptions,
  selectedDocument,
  recentRuns,
}: DocumentAiWorkspaceProps) {
  const [promptOptions, setPromptOptions] = useState<PromptOption[]>(sortPrompts(initialPromptOptions));
  const [editor, setEditor] = useState<EditorState>(initialEditor);
  const [runs, setRuns] = useState<RunInfo[]>(recentRuns);
  const [inputMode, setInputMode] = useState<InputMode>(selectedDocument ? "document" : "text");
  const [sourceText, setSourceText] = useState("");
  const [flash, setFlash] = useState<{ tone: FlashTone; message: string } | null>(null);
  const [savingPrompt, setSavingPrompt] = useState(false);
  const [queueingRun, setQueueingRun] = useState(false);

  const hasSelectedPrompt = Boolean(editor.promptId);
  const usingPresetModel = modelOptions.includes(editor.model);
  const normalizedSourceText = sourceText.trim();
  const activeRuns = runs.filter((run) => run.status === "queued" || run.status === "running");
  const activeRunKey = activeRuns.map((run) => run.id).join(",");

  useEffect(() => {
    if (activeRuns.length === 0) return undefined;

    let cancelled = false;

    const pollRuns = async () => {
      const responses = await Promise.all(
        activeRuns.map(async (run) => {
          const response = await fetch(run.statusUrl, {
            headers: { Accept: "application/json" },
            credentials: "same-origin",
          });

          if (!response.ok) return null;
          const payload = await response.json();
          return payload.run as RunInfo;
        }),
      );

      if (cancelled) return;

      setRuns((currentRuns) =>
        currentRuns.map((run) => responses.find((candidate) => candidate?.id === run.id) || run),
      );
    };

    void pollRuns();
    const intervalId = window.setInterval(() => {
      void pollRuns();
    }, 4000);

    return () => {
      cancelled = true;
      window.clearInterval(intervalId);
    };
  }, [activeRunKey]);

  const updateEditor = (changes: Partial<EditorState>) => {
    setEditor((currentEditor) => ({ ...currentEditor, ...changes }));
  };

  const promptPathFor = (promptId: number) => promptUrlTemplate.replace("__ID__", String(promptId));

  const upsertPrompt = (nextPrompt: PromptOption) => {
    setPromptOptions((currentPrompts) => sortPrompts([...currentPrompts.filter((prompt) => prompt.id !== nextPrompt.id), nextPrompt]));
    setEditor({
      promptId: nextPrompt.id,
      key: nextPrompt.key,
      name: nextPrompt.name,
      description: nextPrompt.description,
      promptBody: nextPrompt.promptBody,
      outputFormat: nextPrompt.outputFormat,
      jsonSchemaText: nextPrompt.jsonSchemaText,
      model: nextPrompt.model,
      active: nextPrompt.active,
    });
  };

  const upsertRun = (nextRun: RunInfo) => {
    setRuns((currentRuns) => [nextRun, ...currentRuns.filter((run) => run.id !== nextRun.id)]);
  };

  const requestJson = async (url: string, method: "POST" | "PATCH", payload: unknown) => {
    const response = await fetch(url, {
      method,
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
        "X-CSRF-Token": authenticityToken,
      },
      credentials: "same-origin",
      body: JSON.stringify(payload),
    });

    const data = await response.json().catch(() => ({}));
    return { response, data };
  };

  const handlePromptSelect = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const promptId = Number(event.target.value);
    if (!Number.isInteger(promptId)) {
      setEditor((currentEditor) => ({ ...currentEditor, promptId: null, key: "", name: "", description: "", promptBody: "", jsonSchemaText: "", outputFormat: "text" }));
      return;
    }

    const selectedPrompt = promptOptions.find((prompt) => prompt.id === promptId);
    if (!selectedPrompt) return;

    setEditor({
      promptId: selectedPrompt.id,
      key: selectedPrompt.key,
      name: selectedPrompt.name,
      description: selectedPrompt.description,
      promptBody: selectedPrompt.promptBody,
      outputFormat: selectedPrompt.outputFormat,
      jsonSchemaText: selectedPrompt.jsonSchemaText,
      model: selectedPrompt.model,
      active: selectedPrompt.active,
    });
    setFlash(null);
  };

  const handleSavePrompt = async (mode: "create" | "update") => {
    setSavingPrompt(true);
    setFlash(null);

    const url = mode === "create" || !editor.promptId ? createPromptUrl : promptPathFor(editor.promptId);
    const method = mode === "create" || !editor.promptId ? "POST" : "PATCH";
    const { response, data } = await requestJson(url, method, {
      document_ai_prompt: {
        key: editor.key,
        name: editor.name,
        description: editor.description,
        prompt_body: editor.promptBody,
        output_format: editor.outputFormat,
        json_schema_text: editor.outputFormat === "json" ? editor.jsonSchemaText : "",
        default_model: editor.model,
        active: editor.active,
      },
    });

    setSavingPrompt(false);

    if (!response.ok) {
      setFlash({ tone: "error", message: normalizeErrors(data) });
      return;
    }

    upsertPrompt(data.prompt as PromptOption);
    setFlash({ tone: "success", message: (data.message as string) || "Prompt saved." });
  };

  const handleQueueRun = async () => {
    if (inputMode === "document" && !selectedDocument) return;
    if (inputMode === "text" && normalizedSourceText.length === 0) {
      setFlash({ tone: "error", message: "Enter text to queue a run." });
      return;
    }

    setQueueingRun(true);
    setFlash(null);

    const { response, data } = await requestJson(createRunUrl, "POST", {
      document_ai_run: {
        document_id: inputMode === "document" ? selectedDocument?.id : null,
        document_ai_prompt_id: editor.promptId,
        model: editor.model,
        output_format: editor.outputFormat,
        prompt_body: editor.promptBody,
        json_schema_text: editor.outputFormat === "json" ? editor.jsonSchemaText : "",
        source_text: inputMode === "text" ? sourceText : "",
      },
    });

    setQueueingRun(false);

    if (!response.ok) {
      setFlash({ tone: "error", message: normalizeErrors(data) });
      return;
    }

    upsertRun(data.run as RunInfo);
    setFlash({ tone: "success", message: (data.message as string) || "Run queued." });
  };

  return (
    <div className="space-y-6">
      {flash ? <div className={flashClasses(flash.tone)}>{flash.message}</div> : null}

      {selectedDocument ? (
        <section className="rounded-2xl border border-neutral-200/80 bg-neutral-50/80 p-4 dark:border-neutral-800 dark:bg-neutral-950/60">
          <p className="text-[0.72rem] font-semibold uppercase tracking-[0.16em] text-neutral-500 dark:text-neutral-400">Selected document</p>
          <div className="mt-3 grid gap-4 md:grid-cols-2 xl:grid-cols-4">
            <div>
              <p className="text-sm text-neutral-500 dark:text-neutral-400">Title</p>
              <p className="mt-1 text-sm font-semibold text-neutral-950 dark:text-white">{selectedDocument.title}</p>
            </div>
            <div>
              <p className="text-sm text-neutral-500 dark:text-neutral-400">Type</p>
              <p className="mt-1 text-sm font-semibold text-neutral-950 dark:text-white">{selectedDocument.typeLabel}</p>
            </div>
            <div>
              <p className="text-sm text-neutral-500 dark:text-neutral-400">Source</p>
              <p className="mt-1 text-sm font-semibold text-neutral-950 dark:text-white">{selectedDocument.sourceLabel}</p>
            </div>
            <div>
              <p className="text-sm text-neutral-500 dark:text-neutral-400">File state</p>
              <p className="mt-1 text-sm font-semibold text-neutral-950 dark:text-white">{selectedDocument.fileStateLabel}</p>
            </div>
          </div>
        </section>
      ) : null}

      <div className="space-y-6">
        <section className="rounded-2xl border border-neutral-200/80 bg-neutral-50/80 p-4 dark:border-neutral-800 dark:bg-neutral-950/60">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <p className="text-[0.72rem] font-semibold uppercase tracking-[0.16em] text-neutral-500 dark:text-neutral-400">Input</p>
              <h3 className="mt-1 text-base font-semibold text-neutral-950 dark:text-white">Source content</h3>
              <p className="mt-1 text-sm text-neutral-500 dark:text-neutral-400">Choose whether this run should use the selected document or pasted text.</p>
            </div>
          </div>

          <div className="mt-4 grid gap-4 lg:grid-cols-[minmax(0,0.8fr)_minmax(0,1.2fr)]">
            <div>
              <p className="text-sm font-medium text-neutral-700 dark:text-neutral-200">Input source</p>
              <div className="mt-2 space-y-3">
                <label className="flex cursor-pointer items-start gap-3 rounded-xl border border-neutral-200/80 bg-white/70 px-4 py-3 text-sm dark:border-neutral-800 dark:bg-neutral-900/70">
                  <input
                    type="radio"
                    name="document-ai-input-mode"
                    className="mt-1 h-4 w-4 border-neutral-300 text-emerald-600 focus:ring-emerald-500"
                    checked={inputMode === "document"}
                    disabled={!selectedDocument}
                    onChange={() => setInputMode("document")}
                  />
                  <span>
                    <span className="block font-medium text-neutral-950 dark:text-white">Selected document</span>
                    <span className="mt-1 block text-neutral-500 dark:text-neutral-400">
                      {selectedDocument ? selectedDocument.title : "Unavailable on this page"}
                    </span>
                  </span>
                </label>

                <label className="flex cursor-pointer items-start gap-3 rounded-xl border border-neutral-200/80 bg-white/70 px-4 py-3 text-sm dark:border-neutral-800 dark:bg-neutral-900/70">
                  <input
                    type="radio"
                    name="document-ai-input-mode"
                    className="mt-1 h-4 w-4 border-neutral-300 text-emerald-600 focus:ring-emerald-500"
                    checked={inputMode === "text"}
                    onChange={() => setInputMode("text")}
                  />
                  <span>
                    <span className="block font-medium text-neutral-950 dark:text-white">Text input</span>
                    <span className="mt-1 block text-neutral-500 dark:text-neutral-400">
                      Paste or type the exact text you want the prompt to use.
                    </span>
                  </span>
                </label>
              </div>
            </div>

            <div>
              <label className="text-sm font-medium text-neutral-700 dark:text-neutral-200">Text input</label>
              <textarea
                className="app-input mt-1 min-h-48 font-mono text-sm"
                rows={10}
                value={sourceText}
                placeholder="Paste document text, notes, or other content here."
                onChange={(event) => setSourceText(event.target.value)}
              />
              <p className="mt-1 text-xs text-neutral-500 dark:text-neutral-400">
                {normalizedSourceText.length > 0
                  ? `${normalizedSourceText.length.toLocaleString()} characters ready`
                  : "Leave blank when using the selected document instead."}
              </p>
            </div>
          </div>
        </section>

        <section className="rounded-2xl border border-neutral-200/80 bg-neutral-50/80 p-4 dark:border-neutral-800 dark:bg-neutral-950/60">
          <div className="flex flex-wrap items-end justify-between gap-3">
            <div>
              <p className="text-[0.72rem] font-semibold uppercase tracking-[0.16em] text-neutral-500 dark:text-neutral-400">Prompt library</p>
              <h3 className="mt-1 text-base font-semibold text-neutral-950 dark:text-white">Reusable prompt</h3>
            </div>

            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                className="app-button-primary"
                onClick={() => void handleQueueRun()}
                disabled={queueingRun || (inputMode === "document" ? !selectedDocument : normalizedSourceText.length === 0)}
              >
                {queueingRun ? "Queueing" : "Queue Run"}
              </button>
              <button type="button" className="app-button-secondary" onClick={() => void handleSavePrompt("create")} disabled={savingPrompt}>
                {savingPrompt ? "Saving" : "Save As New Prompt"}
              </button>
              <button
                type="button"
                className="app-button-secondary"
                onClick={() => void handleSavePrompt("update")}
                disabled={savingPrompt || !hasSelectedPrompt}
              >
                {savingPrompt ? "Saving" : "Update Prompt"}
              </button>
            </div>
          </div>

          <div className="mt-4 max-w-2xl">
            <label className="text-sm font-medium text-neutral-700 dark:text-neutral-200">Load saved prompt</label>
            <select className="app-input mt-1" value={editor.promptId ?? ""} onChange={handlePromptSelect}>
              <option value="">Blank editor</option>
              {promptOptions.map((prompt) => (
                <option key={prompt.id} value={prompt.id}>
                  {prompt.name}
                </option>
              ))}
            </select>
          </div>
        </section>

        <section className="rounded-2xl border border-neutral-200/80 bg-neutral-50/80 p-4 dark:border-neutral-800 dark:bg-neutral-950/60">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <p className="text-[0.72rem] font-semibold uppercase tracking-[0.16em] text-neutral-500 dark:text-neutral-400">Runs</p>
              <h3 className="mt-1 text-base font-semibold text-neutral-950 dark:text-white">Recent history</h3>
            </div>
            <p className="text-sm text-neutral-500 dark:text-neutral-400">{runs.length} shown</p>
          </div>

          {runs.length === 0 ? (
            <div className="mt-4 rounded-xl border border-dashed border-neutral-300 px-4 py-6 text-sm text-neutral-500 dark:border-neutral-700 dark:text-neutral-400">
              No runs have been recorded for this workspace yet.
            </div>
          ) : (
            <div className="mt-4 overflow-x-auto rounded-xl border border-neutral-200/80 dark:border-neutral-800">
              <table className="min-w-full divide-y divide-neutral-200 text-sm dark:divide-neutral-800">
                <thead className="bg-white/80 dark:bg-neutral-900/80">
                  <tr>
                    <th className="px-4 py-3 text-left font-semibold text-neutral-950 dark:text-white">Status</th>
                    <th className="px-4 py-3 text-left font-semibold text-neutral-950 dark:text-white">Prompt</th>
                    <th className="px-4 py-3 text-left font-semibold text-neutral-950 dark:text-white">Model</th>
                    <th className="px-4 py-3 text-left font-semibold text-neutral-950 dark:text-white">Format</th>
                    <th className="px-4 py-3 text-left font-semibold text-neutral-950 dark:text-white">Time taken</th>
                    <th className="px-4 py-3 text-left font-semibold text-neutral-950 dark:text-white">Cost</th>
                    <th className="px-4 py-3 text-right font-semibold text-neutral-950 dark:text-white">Details</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-neutral-200 bg-neutral-50/60 dark:divide-neutral-800 dark:bg-neutral-950/40">
                  {runs.map((run) => (
                    <tr key={run.id} className="transition hover:bg-white/70 dark:hover:bg-neutral-900/60">
                      <td className="px-4 py-3 text-neutral-600 dark:text-neutral-300">
                        <span className={statusClasses(run.status)}>{run.status}</span>
                      </td>
                      <td className="px-4 py-3 text-neutral-600 dark:text-neutral-300">
                        <div className="min-w-48">
                          <p className="font-semibold text-neutral-950 dark:text-white">{run.promptName || `Run #${run.id}`}</p>
                          <p className="mt-1 text-xs text-neutral-500 dark:text-neutral-400">{run.inputLabel}</p>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-neutral-600 dark:text-neutral-300">
                        <span className="block min-w-48 break-words">{run.model}</span>
                      </td>
                      <td className="px-4 py-3 text-neutral-600 dark:text-neutral-300">{run.outputFormat.toUpperCase()}</td>
                      <td className="px-4 py-3 text-neutral-600 dark:text-neutral-300 whitespace-nowrap">{run.durationLabel}</td>
                      <td className="px-4 py-3 text-neutral-600 dark:text-neutral-300 whitespace-nowrap">{run.usageCostLabel}</td>
                      <td className="px-4 py-3 text-right">
                        <a href={run.detailUrl} className="text-sm font-semibold text-emerald-700 dark:text-emerald-300">
                          Open details
                        </a>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>

        <section className="rounded-2xl border border-neutral-200/80 bg-neutral-50/80 p-4 dark:border-neutral-800 dark:bg-neutral-950/60">
          <p className="text-[0.72rem] font-semibold uppercase tracking-[0.16em] text-neutral-500 dark:text-neutral-400">Prompt settings</p>
          <div className="mt-4 grid gap-4 md:grid-cols-2 xl:grid-cols-4">
            <div>
              <label className="text-sm font-medium text-neutral-700 dark:text-neutral-200">Name</label>
              <input className="app-input mt-1" value={editor.name} onChange={(event) => updateEditor({ name: event.target.value })} />
            </div>
            <div>
              <label className="text-sm font-medium text-neutral-700 dark:text-neutral-200">Key</label>
              <input className="app-input mt-1" value={editor.key} onChange={(event) => updateEditor({ key: event.target.value })} />
            </div>
            <div>
              <label className="text-sm font-medium text-neutral-700 dark:text-neutral-200">Output format</label>
              <select
                className="app-input mt-1"
                value={editor.outputFormat}
                onChange={(event) => updateEditor({ outputFormat: event.target.value as EditorState["outputFormat"] })}
              >
                <option value="text">Text</option>
                <option value="json">JSON</option>
              </select>
            </div>
            <div className="flex items-end">
              <label className="inline-flex items-center gap-3 text-sm font-medium text-neutral-700 dark:text-neutral-200">
                <input
                  type="checkbox"
                  className="h-4 w-4 rounded border-neutral-300 text-emerald-600 focus:ring-emerald-500"
                  checked={editor.active}
                  onChange={(event) => updateEditor({ active: event.target.checked })}
                />
                <span>Active in shared library</span>
              </label>
            </div>
            <div className="md:col-span-2">
              <label className="text-sm font-medium text-neutral-700 dark:text-neutral-200">Description</label>
              <textarea
                className="app-input mt-1"
                rows={3}
                value={editor.description}
                onChange={(event) => updateEditor({ description: event.target.value })}
              />
            </div>
            <div>
              <label className="text-sm font-medium text-neutral-700 dark:text-neutral-200">Model preset</label>
              <select
                className="app-input mt-1"
                value={usingPresetModel ? editor.model : ""}
                onChange={(event) => updateEditor({ model: event.target.value })}
              >
                <option value="">Custom override</option>
                {modelOptions.map((modelOption) => (
                  <option key={modelOption} value={modelOption}>
                    {modelOption}
                  </option>
                ))}
              </select>
            </div>
            <div className="xl:col-span-2">
              <label className="text-sm font-medium text-neutral-700 dark:text-neutral-200">Custom model</label>
              <input
                className="app-input mt-1"
                value={usingPresetModel ? "" : editor.model}
                placeholder="Use a non-default Vertex model"
                onChange={(event) => updateEditor({ model: event.target.value })}
              />
            </div>
            <div className="md:col-span-2 xl:col-span-4">
              <label className="text-sm font-medium text-neutral-700 dark:text-neutral-200">Prompt</label>
              <textarea
                className="app-input mt-1 min-h-72 font-mono text-sm"
                rows={14}
                value={editor.promptBody}
                onChange={(event) => updateEditor({ promptBody: event.target.value })}
              />
            </div>
            {editor.outputFormat === "json" ? (
              <div className="md:col-span-2 xl:col-span-4">
                <label className="text-sm font-medium text-neutral-700 dark:text-neutral-200">JSON schema</label>
                <textarea
                  className="app-input mt-1 min-h-56 font-mono text-sm"
                  rows={12}
                  value={editor.jsonSchemaText}
                  placeholder='{"type":"OBJECT","properties":{}}'
                  onChange={(event) => updateEditor({ jsonSchemaText: event.target.value })}
                />
                <p className="mt-1 text-xs text-neutral-500 dark:text-neutral-400">Optional. Leave blank for freeform JSON output.</p>
              </div>
            ) : null}
          </div>
        </section>
      </div>
    </div>
  );
}
